import { APIClientConfig } from '~/interfaces/APIClient';
import axios, { AxiosRequestConfig, AxiosError, AxiosInstance } from 'axios';
import {
  clearAuthLocalStorage, getCsrfToken, getJwtToken, hasTokenExpired,
} from './utils';

/**
* APIClient class used to construct and use instances of axios client.
*
* Configuration:
*   BASE_URL {string} (default: '') - base URL used to create both axios clients
*   API_BASE_PATH {string} (default: '/api/v1') - base path for server API endpoints.
*       Used for constructing other API paths.
*   API_AUTH_PATH {string} (default: '/api/v1/account/user') - path for the authentication
*       API endpoint used for login and logout requests.
*
* If the configuration is retrieved or updated AFTER creating an instance of APIClient, use the
* `updateConfig()` method to update the instance with the new configuration.
*/
class APIClient {
  BASE_URL = '';

  API_BASE_PATH = '/api/v1';

  API_AUTH_PATH = `${this.API_BASE_PATH}/account/user`;

  client!: AxiosInstance;

  baseClient!: AxiosInstance;

  constructor(config: APIClientConfig) {
    this.setup(config);
  }

  private setup({ BASE_URL, API_BASE_PATH, API_AUTH_PATH }: APIClientConfig) {
    if (BASE_URL) this.BASE_URL = BASE_URL;
    if (API_BASE_PATH) this.API_BASE_PATH = API_BASE_PATH;
    if (API_AUTH_PATH) this.API_AUTH_PATH = API_AUTH_PATH;
    this.client = this.constructClient();
    this.baseClient = this.constructBaseClient();
  }

  private getAxiosConfig(): AxiosRequestConfig {
    return {
      baseURL: this.BASE_URL,
      headers: {
        'X-CSRFToken': getCsrfToken(),
        'Content-Type': 'application/json',
        Authorization: '',
      },
    };
  }

  private constructClient() {
    const client = axios.create(this.getAxiosConfig());
    client.interceptors.request.use(this.requestInterceptor.bind(this));
    client.interceptors.response.use(
      (response) => response,
      this.responseErrorInterceptor.bind(this),
    );
    return client;
  }

  private constructBaseClient() {
    return axios.create(this.getAxiosConfig());
  }

  private async requestInterceptor(
    config: AxiosRequestConfig,
  ): Promise<AxiosRequestConfig> {
    const token = getJwtToken();
    if (!token || hasTokenExpired(token) || !config.headers) {
      console.warn('Token missing or expired. Logging out.');
      await this.logoutUser();
      return Promise.reject();
    }

    // eslint-disable-next-line no-param-reassign
    config.headers.Authorization = token ? `JWT ${token}` : '';
    return config;
  }

  private responseErrorInterceptor(error: AxiosError) {
    if (error.response && [401, 403].includes(error.response.status)) {
      return this.logoutUser();
    }

    return Promise.reject(error);
  }

  updateConfig(config: APIClientConfig) {
    this.setup(config);
  }

  async logoutUser() {
    const token = getJwtToken();
    if (token) {
      const config = this.getAxiosConfig();
      config.headers = { ...config.headers, Authorization: `JWT ${token}` };
      await axios.post(`${this.API_AUTH_PATH}/logout/`, null, config).catch((error) => {
        // We are ignoring the error here in case the JWT that is already active is invalid.
        if (error.response?.status === 401) {
          return Promise.resolve();
        }
        throw error;
      });

      clearAuthLocalStorage();
    }
    window.location.reload();
  }
}

export default new APIClient({});
